import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { loadCompanyProfile } from '../utils/companyProfileCache';
import { getUserData, getValidAuthToken, setUserData } from '../utils/api/auth';
import { isCompanyUser } from '../utils/roleUtils';

const CompanyAccessContext = createContext(null);

export const ACCESS_MODES = {
  FULL: 'full',
  BILLING_ONLY: 'billing_only',
  BLOCKED: 'blocked',
};

export const UPGRADE_MODAL_VARIANTS = {
  LOGIN: 'login',
  NAVIGATION: 'navigation',
  API: 'api',
  TRIAL_PENDING: 'trial_pending',
};

export function CompanyAccessProvider({ children }) {
  const [accessMode, setAccessMode] = useState(ACCESS_MODES.FULL);
  const [trialExpired, setTrialExpired] = useState(false);
  const [trialNotStarted, setTrialNotStarted] = useState(false);
  const [trialStartsAt, setTrialStartsAt] = useState(null);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeModalVariant, setUpgradeModalVariant] = useState(UPGRADE_MODAL_VARIANTS.NAVIGATION);

  const applyAccessFromProfile = useCallback((profile) => {
    const mode = profile?.access_mode || ACCESS_MODES.FULL;
    const notStarted = Boolean(profile?.trial_not_started);
    const expired = Boolean(profile?.trial_expired);
    const subExpired = Boolean(profile?.subscription_expired);
    setAccessMode(mode);
    setTrialNotStarted(notStarted);
    setTrialStartsAt(profile?.trial_starts_at || profile?.trial_started_at || null);
    setTrialExpired(expired);
    setSubscriptionExpired(subExpired);

    const stored = getUserData();
    if (stored) {
      setUserData({
        ...stored,
        access_mode: mode,
        trial_not_started: notStarted,
        trial_starts_at: profile?.trial_starts_at || profile?.trial_started_at || null,
        trial_expired: expired,
        subscription_expired: subExpired,
        payment_status: profile?.payment_status ?? stored.payment_status,
      });
    }
  }, []);

  const refreshAccessMode = useCallback(async () => {
    if (!isCompanyUser() || !getValidAuthToken()) {
      setAccessMode(ACCESS_MODES.FULL);
      setTrialExpired(false);
      setTrialNotStarted(false);
      setTrialStartsAt(null);
      setSubscriptionExpired(false);
      return ACCESS_MODES.FULL;
    }

    const profile = await loadCompanyProfile();
    applyAccessFromProfile(profile);
    return profile?.access_mode || ACCESS_MODES.FULL;
  }, [applyAccessFromProfile]);

  const openUpgradeModal = useCallback((variant = UPGRADE_MODAL_VARIANTS.NAVIGATION) => {
    setUpgradeModalVariant(variant);
    setUpgradeModalOpen(true);
  }, []);

  const closeUpgradeModal = useCallback(() => {
    setUpgradeModalOpen(false);
  }, []);

  useEffect(() => {
    if (!isCompanyUser() || !getValidAuthToken()) return undefined;

    const syncOnVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshAccessMode().catch(() => {});
      }
    };

    document.addEventListener('visibilitychange', syncOnVisible);
    return () => document.removeEventListener('visibilitychange', syncOnVisible);
  }, [refreshAccessMode]);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      if (!isCompanyUser() || !getValidAuthToken()) {
        if (mounted) {
          setAccessMode(ACCESS_MODES.FULL);
          setTrialExpired(false);
          setSubscriptionExpired(false);
          setLoading(false);
        }
        return;
      }

      const stored = getUserData();
      if (stored?.access_mode) {
        setAccessMode(stored.access_mode);
        setTrialNotStarted(Boolean(stored.trial_not_started));
        setTrialStartsAt(stored.trial_starts_at || null);
        setTrialExpired(Boolean(stored.trial_expired));
        setSubscriptionExpired(Boolean(stored.subscription_expired));
      }

      try {
        await refreshAccessMode();
      } catch {
        // Keep stored access_mode if profile fetch fails briefly after login
      } finally {
        if (mounted) setLoading(false);
      }
    };

    init();
    return () => {
      mounted = false;
    };
  }, [refreshAccessMode]);

  const value = useMemo(
    () => ({
      accessMode,
      trialExpired,
      trialNotStarted,
      trialStartsAt,
      subscriptionExpired,
      isBillingOnly: accessMode === ACCESS_MODES.BILLING_ONLY,
      loading,
      refreshAccessMode,
      upgradeModalOpen,
      upgradeModalVariant,
      openUpgradeModal,
      closeUpgradeModal,
    }),
    [
      accessMode,
      trialExpired,
      trialNotStarted,
      trialStartsAt,
      subscriptionExpired,
      loading,
      refreshAccessMode,
      upgradeModalOpen,
      upgradeModalVariant,
      openUpgradeModal,
      closeUpgradeModal,
    ]
  );

  return (
    <CompanyAccessContext.Provider value={value}>
      {children}
    </CompanyAccessContext.Provider>
  );
}

export function useCompanyAccess() {
  const ctx = useContext(CompanyAccessContext);
  if (!ctx) {
    throw new Error('useCompanyAccess must be used within CompanyAccessProvider');
  }
  return ctx;
}
